import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import calculateTotal from "@insite/client-framework/Store/Pages/QuickOrder/Handlers/CalculateTotal";
import changeProductQty from "@insite/client-framework/Store/Pages/QuickOrder/Handlers/ChangeProductQty";
import removeProduct from "@insite/client-framework/Store/Pages/QuickOrder/Handlers/RemoveProduct";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardContainer, { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import CardList from "@insite/content-library/Components/CardList";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import BrandBadge from "@tSurya/Components/BrandBadge";
import { MGBW_IMAGE_URL_MEDIUM, MGBW_IMAGE_URL_SMALL, MGBW_IS_CONFIGURABLE } from "@tSurya/Common/MgbwPropertyKeys";
import { stripBrandPrefix } from "@tSurya/Common/Utilities/brandUtils";
import { buildCrossWebsiteProductDetailPath } from "@tSurya/Common/Utilities/websiteUtils";
import ProductDescription from "@tSurya/Overrides/Components/ProductDescription";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design";
import debounce from "lodash/debounce";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const toBrandDto = (brand: any) => {
    if (!brand) {
        return brand;
    }

    if (typeof brand.logoImageAltText === "string") {
        return brand;
    }

    return {
        id: brand.id,
        name: brand.name,
        detailPagePath: brand.detailPagePath,
        logoLargeImagePath: brand.logoLargeImagePath,
        logoSmallImagePath: brand.logoSmallImagePath,
        logoImageAltText: brand.logoAltText,
        urlSegment: "",
    };
};

const getDisplayBrand = (brand: any) => {
    const dto = toBrandDto(brand);
    return dto ? { ...dto, name: stripBrandPrefix(dto.name) } : dto;
};

/** Source brand value the backend sends for MGBW products. */
const MGBW_SOURCE_BRAND = "MG+BW";

interface DescriptionProps {
    product: ProductContextModel;
    onClick?: React.EventHandler<React.MouseEvent>;
    extendedStyles?: ProductDescriptionStyles;
}

export const productDescriptionStyles: ProductDescriptionStyles = {
    productDetailLink: {
        css: css`
            width: 100%;
            color: ${COLORS.text.main} !important;
            text-decoration: none;
            font-weight: 700;

            span {
                color: ${COLORS.text.main} !important;
                text-decoration: none;
                font-weight: 700;
            }

            &:hover {
                color: ${COLORS.text.main} !important;
                text-decoration: underline;
                font-weight: 700;

                span {
                    color: ${COLORS.text.main} !important;
                    text-decoration: none;
                    font-weight: 700;
                }
            }
        `,
        typographyProps: {
            css: css`
                width: 100%;
                ${wrapInContainerStyles}
            `,
        },
    },
};

const mapStateToProps = (state: ApplicationState) => ({
    productInfos: state.pages.quickOrder.productInfos,
    products: state.pages.quickOrder.productInfos.map(o => getProductState(state, o.productId).value) as (
        | ProductModel
        | undefined
    )[],
    total: state.pages.quickOrder.total,
    currencySymbol: state.context.session.currency?.currencySymbol || "",
    showPricing: state.context.showPricing,
    suryaWebsiteUrl: (state.context.settings.properties.suryaWebsiteUrl as string) || "https://www.surya.com",
    globalViewsWebsiteUrl:
        (state.context.settings.properties.globalViewsWebsiteUrl as string) || "https://globalviews.surya.com",
    mgbwWebsiteUrl: (state.context.settings.properties.mgbwWebsiteUrl as string) || "https://mgbw.surya.com",
});

const mapDispatchToProps = {
    calculateTotal,
    changeProductQty,
    removeProduct,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface QuickOrderProductListStyles {
    wrapper?: InjectableCss;
    cardContainer?: CardContainerStyles;
    gridContainer?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    productInfoGridItem?: GridItemProps;
    brand?: ProductBrandStyles;
    productDescription?: ProductDescriptionStyles;
    configurationDetails?: InjectableCss;
    configurationWarningText?: TypographyPresentationProps;
    configurationHelpText?: TypographyPresentationProps;
    brandBadgeWrapper?: InjectableCss;
    priceAndAvailabilityGridItem?: GridItemProps;
    priceAndAvailabilityContainer?: GridContainerProps;
    priceGridItem?: GridItemProps;
    price?: ProductPriceStyles;
    quantityGridItem?: GridItemProps;
    quantityOrdered?: TextFieldProps;
    extendedPriceGridItem?: GridItemProps;
    extendedPriceLabelText?: TypographyPresentationProps;
    extendedPriceText?: TypographyPresentationProps;
    availabilityGridItem?: GridItemProps;
    availability?: ProductAvailabilityStyles;
    buttonsGridItem?: GridItemProps;
    deleteLink?: LinkPresentationProps;
    addToListLink?: ProductAddToListLinkStyles;
    totalText?: TypographyPresentationProps;
}

export const quickOrderProductListStyles: QuickOrderProductListStyles = {
    wrapper: {
        css: css`
            padding-bottom: 20px;
            margin-top: 20px;

            @media (max-width: 768px) {
                padding-left: 15px;
                padding-right: 15px;
            }
        `,
    },
    cardContainer: {
        cardDivider: {
            css: css`
                margin: 0;
                width: 100%;
                border-top: 1px solid ${COLORS.border.main};
                &:last-child {
                    border-bottom: 1px solid ${COLORS.border.main};
                }
            `,
        },
    },
    productImageGridItem: {
        width: [3, 3, 2, 2, 2],
        css: css`
            padding-left: 0;
        `,
    },
    productImage: {
        img: {
            css: css`
                width: 100%;
                min-width: 0;
            `,
        },
    },
    productInfoGridItem: {
        width: [8, 8, 4, 4, 3],
        css: css`
            flex-direction: column;
            gap: 8px;
        `,
    },
    brand: {
        nameText: {
            css: css`
                color: ${COLORS.text.main};
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
                font-size: ${TYPOGRAPHY.size.smaller};
            `,
        },
    },
    productDescription: productDescriptionStyles,
    configurationDetails: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 4px;
        `,
    },
    configurationWarningText: {
        css: css`
            display: block;
            color: #c44b25;
            font-weight: ${TYPOGRAPHY.weight.baseBold};
        `,
    },
    configurationHelpText: {
        css: css`
            display: block;
            font-size: ${TYPOGRAPHY.size.smaller};
            color: ${COLORS.text.main};
        `,
    },
    brandBadgeWrapper: {
        css: css`
            margin-top: 0px;
            width: 90px;
        `,
    },
    priceAndAvailabilityGridItem: {
        width: [12, 12, 3, 3, 5],
    },
    priceAndAvailabilityContainer: {
        gap: 10,
    },
    priceGridItem: {
        width: [4, 4, 12, 12, 5],
        css: css`
            flex-direction: column;
        `,
    },
    price: {
        priceLabelText: {
            variant: "p",
            transform: "uppercase",
            weight: 600,
        },
    },
    quantityGridItem: {
        width: [4, 4, 12, 12, 4],
    },
    quantityOrdered: {
        labelProps: {
            css: css`
                font-size: ${TYPOGRAPHY.size.smaller};
            `,
        },
        cssOverrides: {
            formInputWrapper: css`
                width: 90px;
            `,
        },
    },
    extendedPriceGridItem: {
        width: [4, 4, 12, 12, 3],
        css: css`
            flex-direction: column;
        `,
    },
    extendedPriceLabelText: {
        variant: "p",
        transform: "uppercase",
        weight: 600,
    },
    extendedPriceText: {
        css: css`
            font-weight: bold;
        `,
    },
    availabilityGridItem: {
        width: 12,
    },
    availability: {
        container: {
            css: css`
                margin-top: 5px;
            `,
        },
    },
    buttonsGridItem: {
        width: [12, 12, 3, 3, 2],
        css: css`
            flex-direction: column;
            align-items: center;
        `,
    },
    deleteLink: {
        typographyProps: { size: 12 },
        css: css`
            margin-top: 10px;
            span {
                font-size: ${TYPOGRAPHY.size.smaller} !important;
            }
        `,
    },
    addToListLink: {
        link: {
            css: css`
                margin-top: -10px;
                > span {
                    font-size: ${TYPOGRAPHY.size.smaller} !important;
                }
            `,
        },
    },
    totalText: {
        css: css`
            float: right;
            font-weight: bold;
            margin: 15px 0;
        `,
    },
};

const ProductDescriptionLocal = ({ product, onClick, extendedStyles }: DescriptionProps) => {
    const [localStyles] = useState(() => mergeToNew(productDescriptionStyles, extendedStyles));

    const description = product.product.productTitle || "";
    const productDetailPath = product.productInfo.productDetailPath;

    const endingTerms = description.endsWith("_P") ? "_P" : "-P";
    const productTitle = description.endsWith(endingTerms) ? description.replace(endingTerms, "") : description;

    return productDetailPath ? (
        <Link
            {...localStyles.productDetailLink}
            href={productDetailPath}
            alt={`See product details for ${description}`}
            onClick={onClick}
            data-test-selector="productDescriptionLink"
        >
            {productTitle}
        </Link>
    ) : (
        <Typography {...localStyles.productDetailDescription}>{productTitle}</Typography>
    );
};

const styles = quickOrderProductListStyles;
const StyledSection = getStyledWrapper("section");

const QuickOrderProductList = ({
    products,
    productInfos,
    total,
    currencySymbol,
    calculateTotal,
    changeProductQty,
    removeProduct,
    showPricing,
    suryaWebsiteUrl,
    globalViewsWebsiteUrl,
    mgbwWebsiteUrl,
}: Props) => {
    useEffect(() => calculateTotal(), [products]);

    const removeProductHandler = (productInfo: ProductInfo) => {
        removeProduct({ productId: productInfo.productId, unitOfMeasure: productInfo.unitOfMeasure });
    };

    if (!products || products.length === 0) {
        return null;
    }

    const productListDisplay = products.map((product, index) => {
        if (!product || index >= productInfos.length) {
            return;
        }

        const productInfo = productInfos[index];
        const properties = (product as any)?.properties as Record<string, string> | undefined;

        // Configurable products only exist on MGBW and the Quick Order search response carries no
        // source brand, so derive it here to keep the badge, image and links on the MGBW store.
        const isMgbwConfigurable = properties?.[MGBW_IS_CONFIGURABLE] === "true";
        const sourceBrand = isMgbwConfigurable ? MGBW_SOURCE_BRAND : properties?.sourceBrand;

        const productDetailPath =
            buildCrossWebsiteProductDetailPath(
                productInfo.productDetailPath,
                sourceBrand,
                suryaWebsiteUrl,
                globalViewsWebsiteUrl,
                mgbwWebsiteUrl,
            ) ?? productInfo.productDetailPath;

        // The mgbw_imageUrl* renders are keyed by the default selections and Cylindo answers 404 when
        // one of those values is unavailable for the product, so keep the catalog image as fallback.
        const catalogImagePath = product.mediumImagePath || product.smallImagePath;
        const displayProduct = isMgbwConfigurable
            ? {
                  ...product,
                  properties: { ...properties, sourceBrand: MGBW_SOURCE_BRAND },
                  smallImagePath: properties?.[MGBW_IMAGE_URL_SMALL] || product.smallImagePath,
                  mediumImagePath: properties?.[MGBW_IMAGE_URL_MEDIUM] || product.mediumImagePath,
              }
            : product;

        const productImageStyles =
            isMgbwConfigurable && catalogImagePath
                ? {
                      ...styles.productImage,
                      img: {
                          ...styles.productImage?.img,
                          onError: (event: React.SyntheticEvent<HTMLImageElement>) => {
                              const image = event.currentTarget;
                              image.onerror = null;
                              image.src = catalogImagePath;
                          },
                      },
                  }
                : styles.productImage;

        const productContext: ProductContextModel = {
            product: displayProduct,
            productInfo: { ...productInfo, productDetailPath },
            onQtyOrderedChanged: debounce(qtyOrdered => {
                changeProductQty({
                    qtyOrdered,
                    productId: product.id,
                    unitOfMeasure: productInfo.unitOfMeasure,
                    product,
                });
            },300),
        };

        return (
            <ProductContext.Provider value={productContext} key={product.id + productInfo.unitOfMeasure}>
                <CardContainer extendedStyles={styles.cardContainer}>
                    <GridContainer
                        {...styles.gridContainer}
                        data-test-selector={`QuickOrderProductList_ProductId_${product.id}`}
                    >
                        <GridItem {...styles.productImageGridItem}>
                            <ProductImage product={productContext} extendedStyles={productImageStyles} />
                        </GridItem>
                        <GridItem {...styles.productInfoGridItem}>
                            {!isMgbwConfigurable && product.brand && (
                                <ProductBrand brand={getDisplayBrand(product.brand)} extendedStyles={styles.brand} />
                            )}
                            {isMgbwConfigurable ? (
                                <ProductDescription
                                    product={productContext}
                                    extendedStyles={styles.productDescription}
                                />
                            ) : (
                                <ProductDescriptionLocal
                                    product={productContext}
                                    extendedStyles={styles.productDescription}
                                />
                            )}
                            {isMgbwConfigurable && (
                                <StyledWrapper {...styles.configurationDetails}>
                                    <Typography {...styles.configurationWarningText}>
                                        {translate("Not Configured")}
                                    </Typography>
                                    <Typography {...styles.configurationHelpText}>
                                        {translate("This item can be configured from the cart.")}
                                    </Typography>
                                </StyledWrapper>
                            )}
                            <StyledWrapper {...styles.brandBadgeWrapper}>
                                <BrandBadge brandName={sourceBrand} />
                            </StyledWrapper>
                        </GridItem>
                        <GridItem {...styles.priceAndAvailabilityGridItem}>
                            <GridContainer {...styles.priceAndAvailabilityContainer}>
                                <GridItem {...styles.priceGridItem}>
                                    {!isMgbwConfigurable && (
                                        <ProductPrice
                                            product={productContext}
                                            currencySymbol={currencySymbol}
                                            showSavings={false}
                                            showLabel={true}
                                            label={translate("Unit Price")}
                                            extendedStyles={styles.price}
                                        />
                                    )}
                                </GridItem>
                                <GridItem {...styles.quantityGridItem}>
                                    <ProductQuantityOrdered extendedStyles={styles.quantityOrdered} />
                                </GridItem>
                                <GridItem {...styles.extendedPriceGridItem}>
                                    {!isMgbwConfigurable &&
                                        !product.quoteRequired &&
                                        !productInfo.pricing?.requiresRealTimePrice &&
                                        showPricing && (
                                            <>
                                                <Typography {...styles.extendedPriceLabelText}>
                                                    {translate("Subtotal")}
                                                </Typography>
                                                <Typography
                                                    {...styles.extendedPriceText}
                                                    data-test-selector="quickOrder_ProductPrice"
                                                >
                                                    {productInfo.pricing?.extendedUnitNetPriceDisplay}
                                                </Typography>
                                            </>
                                        )}
                                </GridItem>
                                {!product.quoteRequired && (
                                    <GridItem {...styles.availabilityGridItem}>
                                        <ProductContextAvailability extendedStyles={styles.availability} />
                                    </GridItem>
                                )}
                            </GridContainer>
                        </GridItem>
                        <GridItem {...styles.buttonsGridItem}>
                            <ProductAddToListLink
                                extendedStyles={styles.addToListLink}
                                labelOverride="Add to Project"
                            />
                            <Link
                                {...styles.deleteLink}
                                onClick={() => removeProductHandler(productInfo)}
                                data-test-selector="quickOrder_RemoveProduct"
                            >
                                {translate("Delete")}
                            </Link>
                        </GridItem>
                    </GridContainer>
                </CardContainer>
            </ProductContext.Provider>
        );
    });

    return (
        <>
            <StyledSection {...styles.wrapper}>
                <CardList>{productListDisplay}</CardList>
            </StyledSection>
            {showPricing && (
                <Typography {...styles.totalText} data-test-selector="quickOrder_TotalPrice">
                    {`${translate("Total")}`} <LocalizedCurrency amount={total} />
                </Typography>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(QuickOrderProductList),
    definition: {
        group: "Quick Order",
        icon: "List",
        // allowedContexts: ["QuickOrderPage"],
    },
};

export default widgetModule;
